import { useEffect } from "react"
import { toast } from "react-toastify"
import { useLocation, useRouter } from "@tanstack/react-router"

import { canonicalRoute, TRANSIENT_PARAMS, useFavorites } from "~/hooks/useFavorites"
import { useResolveNavModule } from "~/hooks/useNavModule"
import { trackEvent } from "~/hooks/useTrackingService"
import { encodeViewParam, type ViewTarget } from "~/hooks/useViewRoute"
import { pageTitleFrom, useNavContextStore } from "~/stores/nav-context-store"

const DROPPED_PARAMS = TRANSIENT_PARAMS.filter((key) => key !== "view")

/** Sidebar favorites for the view pills of one page; keeps a renamed current view's favorites in sync. */
export function useViewFavorites(currentView: { id?: number; name: string } | undefined) {
  const router = useRouter()
  const location = useLocation()
  const favorites = useFavorites()
  const resolveNavModule = useResolveNavModule()
  const breadcrumb = useNavContextStore((s) => s.breadcrumb)
  const entityLabel = useNavContextStore((s) => s.entityLabel)
  const tabLabel = useNavContextStore((s) => s.tabLabel)

  // The URL this page would have with `target` active — the favorite's identity and link.
  const routeFor = (target: ViewTarget) =>
    canonicalRoute(
      location.pathname +
        router.options.stringifySearch({ ...location.search, view: encodeViewParam(target) }),
      DROPPED_PARAMS,
    )

  const toggle = (
    target: ViewTarget,
    label: string,
    source: "View Pill" | "Search" | "Hotkey" = "View Pill",
  ) => {
    const route = routeFor(target)
    const navModule = resolveNavModule(route)
    const pageTitle = pageTitleFrom({ breadcrumb, tabLabel, entityLabel })
    const parts = [pageTitle ?? navModule?.pageName ?? navModule?.name, tabLabel]
      .map((part) => part?.trim())
      .filter(Boolean)
    const context = [...new Set(parts)].join(" · ")
    const inFolder = favorites.isInFolder(route)
    const added = favorites.toggle({ route, label, context: context || undefined })
    trackEvent(added ? "Sidebar Favorite Added" : "Sidebar Favorite Removed", {
      "Favorite Type": target && "presetId" in target ? "Preset" : "Saved View",
      Module: navModule?.name,
      "In Folder": inFolder,
      Source: source,
    })
    toast.success(added ? `"${label}" added to favorites` : `"${label}" removed from favorites`)
  }

  const removeView = (viewId: number) => {
    if (favorites.removeView(viewId)) {
      toast.info("This view no longer exists, so it was removed from your favorites")
    }
  }

  const { relabelView } = favorites
  useEffect(() => {
    if (currentView?.id !== undefined && currentView.name) {
      relabelView(currentView.id, currentView.name)
    }
  }, [currentView?.id, currentView?.name, relabelView])

  return {
    isFavorite: (target: ViewTarget) => favorites.isFavorite(routeFor(target)),
    toggle,
    removeView,
  }
}
